import {AxiosError} from "axios";
import {Bindings} from "data/constants/bindings";
import {ModalType} from "data/enums";
import {IApiResponse} from "data/services/http";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {extractErrorMessage, trackSentryErrors} from "data/utils";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable, reaction} from "mobx";
import React, {SyntheticEvent} from "react";
import type {NavigateFunction} from "react-router-dom";
import {AdobeDTM} from "data/utils/AdobeDTM";

interface IForm extends HTMLFormElement {
	terms: HTMLInputElement;
}

interface IParams {
	navigate: NavigateFunction;
}

export interface IModalAcceptTermsRegistrationController extends ViewController<IParams> {
	close: () => void;

	handleFormSubmit: (event: React.SyntheticEvent<IForm>) => void;

	get i18n(): ILocalizationStore;

	get isOpen(): boolean;

	get isDisabled(): boolean;

	get error(): string | undefined;

	get validationMessage(): string | null;
}

@injectable()
export class ModalAcceptTermsRegistrationController
	implements IModalAcceptTermsRegistrationController
{
	@observable private _navigate: NavigateFunction | undefined;
	@observable private _error: string | undefined = "";
	@observable private _validationMessage: string | null = null;
	@observable private _isLoading: boolean = false;
	private _disposers: ReturnType<typeof reaction>[] = [];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get error(): string | undefined {
		return this._error;
	}

	get validationMessage(): string | null {
		return this._validationMessage;
	}

	get isDisabled(): boolean {
		return this._isLoading;
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.ACCEPT_TERMS;
	}

	public close = () => {
		this._modalsStore.hideModal();
		this._navigate?.("/play");
	};

	dispose(): void {
		return;
	}

	init(param: IParams): void {
		this._navigate = param.navigate;
		this._disposers = [
			reaction(
				() => this.isOpen,
				(isOpen) => {
					if (isOpen) {
						AdobeDTM.trackAction({
							"action.name": "Spinball: Accept Terms Impression",
						});
					}
				}
			),
		];
	}

	onChange(param: IParams): void {
		this._navigate = param.navigate;
	}

	@action
	public handleFormSubmit = (event: SyntheticEvent<IForm>) => {
		event.preventDefault();
		const {terms} = event.currentTarget;

		if (!terms.checked) {
			return;
		}

		this._isLoading = true;
		this._userStore
			.acceptTerms()
			.then(this.close)
			.catch(this.onError)
			.finally(() => {
				this._isLoading = false;
			});
	};

	@action
	private onError = (error: AxiosError<IApiResponse>) => {
		this._error = extractErrorMessage(error);
		trackSentryErrors(error, {}, "registration");
	};
}
